/**
 * @fileoverview Org portal — a program's optional listing details.
 *
 * Instruments, location, pay and when it runs used to be part of creating a
 * program. They are not any more: the intake asks only what every
 * program needs, and these live here for the orgs that want them advertised.
 *
 * Blank is meaningful. A field left empty is omitted from the marketplace
 * listing entirely rather than rendered as an empty label, so a program that
 * fills in none of this reads as a clean short listing.
 *
 * The instructor brief is the exception to "listing": it is never advertised.
 * It is what somebody needs in order to turn up and teach — where to park,
 * which door, who to ask for — and it reaches the people teaching the program
 * and nobody else. It is its own form with its own Save, so a brief can be
 * fixed without re-sending the listing, and it takes bold, italic and
 * underline: stored as the same lesson markdown every client renders
 * (`lib/richText.js`), so the instructor's portal and the iOS app show it
 * the way it was written.
 *
 * Who instructors contact lives on the program's Overview
 * (`ProgramContactsCard`), with the school's people and the org's own.
 */

import { useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, Button } from "@/components/studio/primitives";
import RichTextEditor from "@/components/studio/RichTextEditor";

/** The brief's toolbar: minor font formatting, nothing structural. */
const BRIEF_ACTIONS = ["bold", "italic", "underline"];

const BRIEF_PLACEHOLDER = "Point of contact, parking, which door to use, what to bring…";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

const splitList = (raw) => raw.split(",").map((s) => s.trim()).filter(Boolean);

/** "" means "not set" to the backend; sending "" would publish an empty line. */
const orNull = (v) => (v || "").trim() || null;

/**
 * What instructors need to know: its own card, its own Save.
 *
 * The saved value is what the editor last emitted; Save is enabled only once
 * it differs from what is stored, so an unchanged brief is never re-sent and
 * never announced to the program's instructors as "updated".
 */
function InstructorBrief({ program, onSaved }) {
  const [brief, setBrief] = useState(program.instructor_brief || "");
  const [saved, setSaved] = useState(program.instructor_brief || "");
  const [saving, setSaving] = useState(false);
  const dirty = brief.trim() !== saved.trim();

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, { instructor_brief: brief.trim() });
      const next = res.data?.program;
      setSaved(next?.instructor_brief ?? brief.trim());
      toast.success("Saved what instructors need to know");
      onSaved?.(next);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save what instructors need to know"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="program-brief-settings">
      <Card className="mb-6">
        <SectionHeader>What instructors need to know</SectionHeader>
        <p className="mb-3 font-sans text-[13px] text-[var(--text-tertiary)]">
          Shown to everyone teaching this program, on their own portal. Where to park, which entrance, who to
          ask for at the door, what to bring — anything they need in order to turn up and teach. Never shown on
          the public listing.
        </p>
        <RichTextEditor
          value={brief}
          onChange={setBrief}
          actions={BRIEF_ACTIONS}
          placeholder={BRIEF_PLACEHOLDER}
          ariaLabel="What instructors need to know"
          minHeight={160}
          className="font-sans text-[15px] leading-relaxed"
          testId="settings-instructor-brief"
        />
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={saving || !dirty} data-testid="settings-brief-save">
            {saving ? "Saving…" : "Save"}
          </Button>
          {dirty && !saving && (
            <span className="font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="settings-brief-unsaved">
              Unsaved changes
            </span>
          )}
        </div>
      </Card>
    </form>
  );
}

export default function ProgramListingSettings({ program, onSaved }) {
  const [form, setForm] = useState({
    instruments: (program.instruments || []).join(", "),
    location: program.location || "",
    compensation: program.compensation || "",
    meeting_times: program.meeting_times || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, {
        instruments: splitList(form.instruments),
        location: orNull(form.location),
        compensation: orNull(form.compensation),
        meeting_times: orNull(form.meeting_times),
      });
      toast.success("Listing details saved");
      onSaved?.(res.data?.program);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save the listing details"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <InstructorBrief program={program} onSaved={onSaved} />

      <form onSubmit={submit} data-testid="program-listing-settings">
        <Card className="mb-6">
          <SectionHeader>Listing details</SectionHeader>
          <p className="mb-4 font-sans text-[13px] text-[var(--text-tertiary)]">
            Optional. Anything you fill in appears on the listing instructors browse; anything you leave blank is
            left off it entirely.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              value={form.instruments}
              onChange={set("instruments")}
              placeholder="Instruments (comma-separated)"
              data-testid="settings-instruments"
              className={inputClass}
            />
            <div>
              <input
                value={form.location}
                onChange={set("location")}
                placeholder="Location (town or address)"
                data-testid="settings-location"
                className={inputClass}
              />
              {/* The only field here with an invisible side effect: the town is
                  geocoded so instructors can find the program by distance. */}
              <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
                Used to place this program on the instructor map.
              </p>
            </div>
            <input
              value={form.compensation}
              onChange={set("compensation")}
              placeholder="Compensation (e.g. $45/hr)"
              data-testid="settings-compensation"
              className={inputClass}
            />
            <input
              value={form.meeting_times}
              onChange={set("meeting_times")}
              placeholder="When it runs (e.g. Tue + Thu, 4–6pm)"
              data-testid="settings-meeting-times"
              className={inputClass}
            />
          </div>

          <div className="mt-5">
            <Button type="submit" disabled={saving} data-testid="settings-save">
              {saving ? "Saving…" : "Save listing details"}
            </Button>
          </div>
        </Card>
      </form>
    </>
  );
}
